import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

const servicesData = {
  'direct-taxation': {
    title: 'Direct Taxation',
    subtitle: 'Expert Income Tax Solutions',
    description: 'Comprehensive income tax planning, return filing, and tax advisory services for individuals and businesses to optimize tax liabilities and ensure full compliance with tax laws.',
    image: 'https://images.unsplash.com/photo-1586486855514-8c633cc6fd38?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'Income Tax Return Filing (ITR-1 to ITR-7)',
      'Tax Planning & Advisory',
      'TDS Compliance & Returns',
      'Tax Assessment & Appeals',
      'Advance Tax Calculation',
      'Capital Gains Tax Planning',
      'HUF Tax Planning',
      'Senior Citizen Tax Benefits'
    ],
    benefits: [
      'Minimize tax liability legally',
      'Avoid penalties and notices',
      'Expert handling of complex cases',
      'Year-round tax support'
    ],
    detailedContent: `Our Direct Taxation services cover the entire spectrum of income tax requirements for individuals, HUFs, firms, and companies. We ensure timely filing, accurate computation, and strategic planning to help you save taxes within the legal framework.

Whether you're a salaried individual, a business owner, or have complex income sources including capital gains, rental income, or foreign income, our team has the expertise to handle your tax matters efficiently.

We also assist in responding to income tax notices, representing clients before tax authorities, and handling assessment proceedings to ensure the best possible outcomes.`
  },
  'indirect-taxation-gst': {
    title: 'Indirect Taxation & GST',
    subtitle: 'Complete GST Compliance Solutions',
    description: 'Expert GST registration, return filing, compliance, and advisory services to navigate the complex indirect tax landscape with ease.',
    image: 'https://images.unsplash.com/photo-1611125832047-1d7ad1e8e48f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'GST Registration & Amendments',
      'Monthly/Quarterly GST Returns',
      'GST Annual Returns (GSTR-9)',
      'GST Audit (GSTR-9C)',
      'Input Tax Credit Optimization',
      'E-Way Bill Compliance',
      'GST Refund Claims',
      'GST Litigation Support'
    ],
    benefits: [
      'Maximize input tax credit',
      'Ensure timely compliance',
      'Avoid interest and penalties',
      'Expert dispute resolution'
    ],
    detailedContent: `Goods and Services Tax (GST) has transformed India's indirect tax landscape. Our comprehensive GST services ensure your business stays compliant while maximizing available benefits.

From registration to monthly returns, annual compliance to audits, we handle all aspects of GST for businesses of all sizes. Our expertise includes complex scenarios like multi-state operations, export/import transactions, and e-commerce compliance.

We proactively identify input tax credit opportunities and help resolve any GST-related disputes with authorities through proper documentation and representation.`
  },
  'audit-assurance': {
    title: 'Audit & Assurance',
    subtitle: 'Professional Audit Services',
    description: 'Professional audit services including statutory audit, tax audit, internal audit, and other assurance services to ensure accuracy and compliance.',
    image: 'https://images.unsplash.com/photo-1574884280706-7342ca3d4231?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'Statutory Audit',
      'Tax Audit (Section 44AB)',
      'Internal Audit',
      'Bank Audit',
      'Stock Audit',
      'Concurrent Audit',
      'Revenue Audit',
      'Forensic Audit'
    ],
    benefits: [
      'Independent verification',
      'Risk identification',
      'Process improvement insights',
      'Stakeholder confidence'
    ],
    detailedContent: `Our audit and assurance services provide independent verification of financial statements and business processes. We help organizations identify risks, improve controls, and maintain stakeholder confidence.

Statutory audits ensure compliance with Companies Act requirements, while tax audits under Section 44AB verify the correctness of income reported. Our internal audit services help businesses strengthen their internal controls and operational efficiency.

We also conduct specialized audits including bank branch audits, stock audits for lending institutions, and forensic audits for fraud investigation.`
  },
  'business-setup': {
    title: 'Business Setup Services',
    subtitle: 'Start Your Business Right',
    description: 'End-to-end support for business registration, incorporation, and compliance for startups and established companies.',
    image: 'https://images.unsplash.com/photo-1704440349159-e7f8a6db589f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'Private Limited Company Registration',
      'LLP Registration & Conversion',
      'One Person Company (OPC)',
      'Partnership Firm Registration',
      'Proprietorship Registration',
      'Trade License & Shop Act',
      'MSME/Udyam Registration',
      'Startup India Registration'
    ],
    benefits: [
      'Hassle-free incorporation',
      'Expert guidance on structure',
      'All licenses under one roof',
      'Post-incorporation support'
    ],
    detailedContent: `Starting a business involves crucial decisions about the right legal structure. Our business setup services guide you through choosing the optimal structure based on your business goals, tax implications, and compliance requirements.

We handle the complete incorporation process including name approval, document drafting, filing with registrar, and obtaining necessary licenses. Our team ensures your business is set up correctly from day one.

Post-incorporation, we provide ongoing compliance support to keep your business in good standing with regulatory authorities.`
  },
  'corporate-law': {
    title: 'Corporate Law Services',
    subtitle: 'Complete Corporate Compliance',
    description: 'Complete corporate compliance solutions including ROC filings, board meetings, annual returns, and statutory registers maintenance.',
    image: 'https://images.unsplash.com/photo-1760346547345-55dc606f9cbb?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'Annual ROC Filings',
      'Board Meeting Compliance',
      'AGM & EGM Management',
      'Director KYC & Changes',
      'Share Transfer & Allotment',
      'Registered Office Change',
      'Increase in Authorized Capital',
      'Company Closure/Strike Off'
    ],
    benefits: [
      'Stay compliant always',
      'Avoid penalties and prosecution',
      'Expert company secretary support',
      'Timely reminder system'
    ],
    detailedContent: `Corporate compliance is crucial for the smooth functioning and legal standing of any company. Our corporate law services ensure your company meets all statutory requirements under the Companies Act.

We manage the complete compliance calendar including board meetings, annual general meetings, ROC filings, and statutory register maintenance. Our team sends timely reminders to ensure no deadline is missed.

We also handle complex corporate actions like share allotment, director changes, capital restructuring, and company closure with proper legal documentation and regulatory filings.`
  },
  'accounting-bookkeeping': {
    title: 'Accounting & Bookkeeping',
    subtitle: 'Accurate Financial Records',
    description: 'Accurate and timely bookkeeping, financial statement preparation, and accounting advisory services for businesses of all sizes.',
    image: 'https://images.unsplash.com/photo-1753955900083-b62ee8d97805?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'Books of Accounts Maintenance',
      'Financial Statement Preparation',
      'MIS Reports & Dashboards',
      'Payroll Processing',
      'Bank Reconciliation',
      'Accounts Receivable/Payable',
      'Inventory Accounting',
      'Cash Flow Management'
    ],
    benefits: [
      'Real-time financial visibility',
      'Informed decision making',
      'Cost-effective outsourcing',
      'Accurate compliance data'
    ],
    detailedContent: `Accurate bookkeeping is the foundation of sound financial management and compliance. Our accounting services ensure your books are maintained accurately, timely, and in accordance with applicable accounting standards.

We offer both on-site and remote bookkeeping services using modern accounting software. Our MIS reports provide valuable insights into your business performance, helping you make informed decisions.

Payroll processing services include salary computation, statutory deductions, payslip generation, and compliance with PF, ESI, and professional tax requirements.`
  },
  'nri-services': {
    title: 'NRI Services',
    subtitle: 'Specialized NRI Tax & Compliance',
    description: 'Specialized tax and compliance services for Non-Resident Indians including property transactions and fund repatriation.',
    image: 'https://images.unsplash.com/photo-1704440374771-26f4f5498f26?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'NRI Income Tax Returns',
      'FEMA Compliance & Advisory',
      'Property Sale Transactions',
      'Capital Gains Tax Planning',
      'Fund Repatriation',
      'Form 15CA/15CB Certification',
      'Double Taxation Relief',
      'Investment Advisory'
    ],
    benefits: [
      'Expert NRI tax knowledge',
      'FEMA compliant transactions',
      'Hassle-free repatriation',
      'Remote service delivery'
    ],
    detailedContent: `Managing finances in India while living abroad can be challenging. Our specialized NRI services address the unique tax and compliance needs of Non-Resident Indians.

We handle NRI taxation including income from property, capital gains from asset sales, and investment income. Our FEMA expertise ensures your transactions comply with foreign exchange regulations.

Property sale transactions require careful tax planning and documentation including Form 15CA/15CB for repatriation. We manage the entire process to ensure smooth and compliant fund transfers abroad.`
  },
  'business-advisory': {
    title: 'Business Advisory',
    subtitle: 'Strategic Financial Guidance',
    description: 'Strategic financial advisory, business planning, funding assistance, and growth consulting services to help your business thrive.',
    image: 'https://images.unsplash.com/photo-1581374820583-317d45555a82?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    features: [
      'Business Planning & Strategy',
      'Financial Projections',
      'Funding & Loan Assistance',
      'Business Valuation',
      'Due Diligence Services',
      'Merger & Acquisition Support',
      'Restructuring Advisory',
      'Performance Improvement'
    ],
    benefits: [
      'Strategic growth guidance',
      'Access to funding opportunities',
      'Expert negotiation support',
      'Data-driven decisions'
    ],
    detailedContent: `Beyond compliance, businesses need strategic guidance to grow and succeed. Our business advisory services provide the expertise and insights needed to make informed decisions.

Whether you're seeking funding, planning expansion, or exploring M&A opportunities, our team provides comprehensive support including business valuation, due diligence, and negotiation assistance.

We help businesses identify growth opportunities, optimize operations, and navigate challenges with strategic planning and financial modeling support.`
  }
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const service = servicesData[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')} className="bg-teal-600 hover:bg-teal-700">
            Go Back Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const otherServices = Object.entries(servicesData)
    .filter(([id]) => id !== serviceId)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 to-slate-800 py-20">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={service.image} 
            alt={service.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-teal-400 hover:text-teal-300 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
          
          <span className="text-teal-400 font-semibold text-sm uppercase tracking-wide">
            {service.subtitle}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
            {service.title}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl">
            {service.description}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Service Image */}
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-80 object-cover"
                />
              </div>

              {/* Detailed Content */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">About This Service</h2>
                <div className="prose prose-slate max-w-none">
                  {service.detailedContent.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-slate-600 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              {/* What We Offer */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">What We Offer</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-slate-100">
                      <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-8">
              {/* Benefits Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Key Benefits</h3>
                <ul className="space-y-4">
                  {service.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mt-2"></div>
                      <span className="text-slate-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-4">Need This Service?</h3>
                <p className="text-teal-100 mb-6">
                  Get in touch with us for a free consultation and let us help you with your {service.title.toLowerCase()} needs.
                </p>
                <Button 
                  onClick={scrollToContact}
                  className="w-full bg-white text-teal-700 hover:bg-teal-50"
                >
                  Contact Us Now
                </Button>
                <div className="mt-4 flex items-center justify-center gap-2 text-teal-100">
                  <Phone className="w-4 h-4" />
                  <a href="tel:9636557944" className="hover:text-white">+91 9636557944</a>
                </div>
              </div>

              {/* Other Services */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Other Services</h3>
                <ul className="space-y-3">
                  {otherServices.map(([id, svc]) => (
                    <li key={id}>
                      <button
                        onClick={() => navigate(`/services/${id}`)}
                        className="w-full text-left flex items-center justify-between p-3 rounded-lg hover:bg-teal-50 transition-colors group"
                      >
                        <span className="text-slate-700 group-hover:text-teal-600">{svc.title}</span>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServiceDetail;
